import { useMemo } from 'react';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { LinkingType, RoomDto } from '@/types';
import { getCoreApiUrl, request } from '@/utils/ajax';
import { createQueryHook, useGetActiveProject } from '@/utils/hooks';
import { searchOptionGroups, updateRoomOptionGroupLinks } from '@/services/options/option-groups';

export function getRooms(projectId: number): Promise<RoomDto[]> {
    return request(`v1/project/${projectId}/rooms`, {}, getCoreApiUrl()).then((x) => x.json());
}

export const useRooms = createQueryHook(getRooms);

export function useRoomMap(projectId: number | null) {
    const { data: rooms } = useRooms.withOptions({ enabled: !!projectId }, projectId!);

    return useMemo(() => {
        if (!rooms) {
            return null;
        }

        const map = new Map<number, RoomDto>();
        rooms.forEach((r) => map.set(r.id, r));
        return map;
    }, [rooms]);
}

/** Buyer-facing room metadata (seller-only fields omitted by the API). */
export interface BuyerRoomDto {
    id: number;
    sortOrder: number | null;
    name: string;
    buyerDescription: string;
    icon: IconName;
}

export function getBuyerRooms(residentialUnitId: number): Promise<BuyerRoomDto[]> {
    return request(`v1/buyer/${residentialUnitId}/rooms`, {}, getCoreApiUrl()).then((x) => x.json());
}

export const useBuyerRooms = createQueryHook(getBuyerRooms);

export function useBuyerRoomMap(residentialUnitId: number | null) {
    const { data: rooms } = useBuyerRooms.withOptions({ enabled: !!residentialUnitId }, residentialUnitId!);

    return useMemo(() => {
        if (!rooms) {
            return null;
        }

        const map = new Map<number, BuyerRoomDto>();
        rooms.forEach((r) => map.set(r.id, r));
        return map;
    }, [rooms]);
}

export function updateRoom(projectId: number, roomId: number, room: RoomRequest): Promise<RoomDto> {
    return request(
        `v1/project/${projectId}/rooms/${roomId}`,
        {
            method: 'PUT',
            json: true,
            body: room,
        },
        getCoreApiUrl()
    ).then((x) => x.json());
}

export function createRoom(projectId: number, room: RoomRequest): Promise<RoomDto> {
    return request(
        `v1/project/${projectId}/rooms`,
        {
            method: 'POST',
            json: true,
            body: room,
        },
        getCoreApiUrl()
    ).then((x) => x.json());
}

export function deleteRoom(projectId: number, roomId: number): Promise<any> {
    return request(
        `v1/project/${projectId}/rooms/${roomId}`,
        {
            method: 'DELETE',
        },
        getCoreApiUrl()
    );
}


export function reorderRooms(projectId: number, orderedIds: number[]): Promise<any> {
    return request(
        `v1/project/${projectId}/rooms/order`,
        {
            method: 'POST',
            json: true,
            body: orderedIds,
        },
        getCoreApiUrl()
    );
}

export interface RoomRequest {
    sortOrder: number;
    name: string;
    labelNote?: string | null;
    sellerDescription: string;
    buyerDescription: string;
    icon: string;

    unitsToLink: number[];
    unitsToUnlink: number[];
}

function generateCopyName(baseName: string, existingNames: string[]): string {
    const existing = new Set(existingNames.map((n) => n.toLowerCase()));

    // Strip existing copy suffixes to get the original name
    const originalName = baseName.replace(/ - Copy( \d+)?$/i, '');

    const first = `${originalName} - Copy`;
    if (!existing.has(first.toLowerCase())) {
        return first;
    }

    let counter = 2;
    while (existing.has(`${originalName} - Copy ${counter}`.toLowerCase())) {
        counter++;
    }
    return `${originalName} - Copy ${counter}`;
}

export async function copyRoomWithOptionGroups(
    projectId: number,
    sourceRoom: RoomDto,
    sortOrder: number,
    existingRoomNames: string[]
): Promise<RoomDto> {
    const newRoom = await createRoom(projectId, {
        sortOrder,
        name: generateCopyName(sourceRoom.name, existingRoomNames),
        labelNote: sourceRoom.labelNote,
        sellerDescription: sourceRoom.sellerDescription,
        buyerDescription: sourceRoom.buyerDescription,
        icon: sourceRoom.icon,
        unitsToLink: [],
        unitsToUnlink: [],
    });

    const optionGroups = await searchOptionGroups({ projectId, rooms: [sourceRoom.id] });
    const roomLinkedGroups = optionGroups.filter((og) => og.linkType === LinkingType.Room);

    await Promise.allSettled(
        roomLinkedGroups.map((og) => {
            const sourceLink = og.linkedRooms.find((l) => l.roomId === sourceRoom.id);
            const existing = og.linkedRooms.map((l) => ({
                roomId: l.roomId,
                pricingFieldId: l.pricingFieldId,
            }));
            return updateRoomOptionGroupLinks(og.id, {
                links: [
                    ...existing,
                    { roomId: newRoom.id, pricingFieldId: sourceLink?.pricingFieldId ?? null },
                ],
            });
        })
    );

    return newRoom;
}
